'use client';

import { Suspense, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { safeReturnTo } from '@/lib/safe-return-to';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Cake } from 'lucide-react';

function SignupForm() {
  const searchParams = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const canUseDevAuth = process.env.NODE_ENV === 'development';

  const saveReturnTo = () => {
    if (returnTo) {
      sessionStorage.setItem('sweetspot_return_to', returnTo);
    }
  };

  const createDevSession = async (): Promise<boolean> => {
    if (!canUseDevAuth) {
      return false;
    }
    const normalizedEmail = email.trim().toLowerCase();

    const res = await fetch('/api/proxy/auth/dev-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail, password }),
    });

    if (!res.ok) {
      return false;
    }

    const data = await res.json();
    document.cookie = `sweetspot_dev_auth=${encodeURIComponent(JSON.stringify({ userId: data.userId, email }))};path=/;max-age=${60 * 60 * 24 * 7}`;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const API_URL = '/api/proxy';
      const signupRes = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email: normalizedEmail, password }),
      });

      if (!signupRes.ok) {
        const data = await signupRes.json();
        const message =
          typeof data?.message === 'string' ? data.message : 'Failed to create account';
        const isAlreadyRegistered = message
          .toLowerCase()
          .includes('user already registered');
        if (isAlreadyRegistered) {
          const supabase = createClient();
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password,
          });
          if (!signInError) {
            saveReturnTo();
            window.location.href = returnTo ?? '/dashboard';
            return;
          }
          setError('This email is already registered. Please sign in or reset your password.');
          setLoading(false);
          return;
        }
        setError(message);
        setLoading(false);
        return;
      }

      const result = await signupRes.json();
      saveReturnTo();

      if (result.mode === 'admin') {
        const supabase = createClient();
        const { error: signInError } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
        if (signInError) { setError(signInError.message); setLoading(false); return; }
        window.location.href = '/onboarding';
        return;
      }

      if (result.mode === 'anon') {
        if (result.emailConfirmRequired) {
          setError('Account created! Please check your email to confirm your address, then sign in.');
          setLoading(false);
          return;
        }
        const supabase = createClient();
        const { error: signInError } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
        if (signInError) { setError(signInError.message); setLoading(false); return; }
        window.location.href = '/onboarding';
        return;
      }

      if (result.mode === 'dev') {
        const sessionCreated = await createDevSession();
        if (!sessionCreated) {
          setError('Dev auth is disabled. Enable AUTH_DEV_BYPASS_ENABLED=true in development to use local auth.');
          setLoading(false);
          return;
        }
        window.location.href = '/onboarding';
        return;
      }

      window.location.href = '/onboarding';
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Cake className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">Create your account</CardTitle>
        <CardDescription>Start tracking your bakery finances</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
          <div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required /></div>
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" placeholder="At least 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} /></div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Creating account...' : 'Sign Up'}</Button>
          <p className="text-sm text-muted-foreground">Already have an account?{' '}<Link href={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'} className="text-primary hover:underline">Sign in</Link></p>
        </CardFooter>
      </form>
    </Card>
  );
}

export default function SignupPage() {
  return (
    <Suspense>
      <SignupForm />
    </Suspense>
  );
}
